import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import { useAppSettings } from "@/lib/settings";
import { Phone, ShieldCheck } from "lucide-react";
import { sendPhoneOtp, verifyPhoneOtp, resumePhoneLogin } from "@/lib/twilio-auth.functions";
import { verifyWithResume } from "@/lib/auth-verify-flow";

import { trackCall } from "@/lib/diagnostics/instrument";
import { startCorrelation, endCorrelation } from "@/lib/diagnostics/correlation";
import { report, recordPerf } from "@/lib/diagnostics/report";
import { DIAG_CODES } from "@/lib/diagnostics/types";

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Sign in with mobile OTP — Brothers Club" },
      { name: "description", content: "Sign in to Brothers Club with your Indian mobile number and a one-time password to play daily games, manage your wallet, and refer friends." },
      { property: "og:title", content: "Sign in with mobile OTP — Brothers Club" },
      { property: "og:description", content: "Sign in with your mobile number to play daily games on Brothers Club." },
      { property: "og:url", content: "https://brothersclub.co.in/auth" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://brothersclub.co.in/auth" }],
  }),
  component: AuthPage,
});


function AuthPage() {
  return <AuthContent />;
}

function AuthContent() {
  const navigate = useNavigate();
  const { data: settings } = useAppSettings();
  const [step, setStep] = useState<"phone" | "otp">("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) navigate({ to: "/" });
    }).catch(() => {});
  }, [navigate]);


  const fullPhone = phone.startsWith("+") ? phone : `+91${phone.replace(/\D/g, "")}`;

  async function sendOtp() {
    if (phone.replace(/\D/g, "").length < 10) return toast.error("Enter a valid mobile number");
    setLoading(true);
    const res = await trackCall(() => sendPhoneOtp({ data: { phone: fullPhone } }), {
      category: "auth",
      stage: "otp_send_request",
    });
    setLoading(false);
    if (!res.ok) {
      toast.error(`${res.safe_message} Error ID: ${res.error_id}`);
      return;
    }
    toast.success("OTP sent to your mobile");
    setStep("otp");
  }

  function fail(stage: string, code: string, message?: string) {
    const { error_id, safe_message } = report({
      category: "auth",
      code,
      stage,
      technical_message: message,
      severity: "error",
    });
    toast.error(`${safe_message} Error ID: ${error_id}`);
  }

  async function verifyOtp() {
    if (otp.length < 6) return toast.error("Enter 6-digit OTP");
    setLoading(true);
    const started = Date.now();
    // One correlation ID for the whole verify action, so the admin panel can
    // replay client -> server -> provider -> response for this exact attempt.
    startCorrelation("otp_verify", { stage: "twilio_verify" });
    try {

      // The native shell proxies server-fn RPCs across origins; occasionally the
      // response body is lost and arrives empty. Retrying is safe (the server
      // remembers the Twilio approval); if every body is lost, resume sign-in
      // from that recorded approval instead of dead-ending the user.
      const callErrorRef: { current: { safe_message: string; error_id: string } | null } = {
        current: null,
      };


      const outcome = await verifyWithResume({
        verify: async () => {
          const call = await trackCall(
            () => verifyPhoneOtp({ data: { phone: fullPhone, code: otp } }),
            { category: "auth", stage: "twilio_verify" },
          );
          if (!call.ok) {
            callErrorRef.current = { safe_message: call.safe_message, error_id: call.error_id };
            throw new Error(call.code);
          }
          return call.data;
        },
        resume: async () => {
          const call = await trackCall(
            () => resumePhoneLogin({ data: { phone: fullPhone, code: otp } }),
            { category: "auth", stage: "twilio_verify_resume" },
          );
          return call.ok ? call.data : null;
        },
        attempts: 2,
      });

      if (outcome.outcome === "call_error") {
        if (callErrorRef.current) {
          toast.error(`${callErrorRef.current.safe_message} Error ID: ${callErrorRef.current.error_id}`);
          return;
        }
        return fail("twilio_verify", DIAG_CODES.malformed_response, "verify call failed");
      }

      if (outcome.outcome === "malformed") {
        return fail(
          "twilio_verify",
          DIAG_CODES.malformed_response,
          `unreadable verify response (${outcome.shape ?? "unknown shape"})`,
        );
      }

      const res: any = outcome.outcome === "ok" ? outcome.result : outcome.result;




      if (res.ok !== true) {
        const code =
          res.code === "invalid_code"
            ? DIAG_CODES.otp_invalid
            : res.code === "expired_code"
              ? DIAG_CODES.otp_expired
              : res.code === "auth_user_lookup_failed"
                ? DIAG_CODES.account_lookup_failed
                : res.code === "auth_user_create_failed"
                  ? DIAG_CODES.account_create_failed
                  : res.code === "magic_link_generation_failed"
                    ? DIAG_CODES.magic_link_failed
                    : res.code === "missing_token_hash"
                      ? DIAG_CODES.missing_token_hash
                      : DIAG_CODES.session_exchange_failed;
        return fail(String(res.code ?? "twilio_verify"), code, res.message);
      }
      if (typeof res.token_hash !== "string" || res.token_hash.length === 0) {
        return fail("token_hash_validation", DIAG_CODES.missing_token_hash);
      }

      const { data: session, error } = await supabase.auth.verifyOtp({
        token_hash: res.token_hash,
        type: "magiclink",
      });
      if (error || !session?.session) {
        const already = (await supabase.auth.getSession()).data.session;
        if (!already) {
          return fail(
            "supabase_token_exchange",
            DIAG_CODES.session_exchange_failed,
            error?.message,
          );
        }
      }

      recordPerf("auth_restore", Date.now() - started, { metadata: { stage: "otp_login" } });
      toast.success("Welcome!");
      navigate({ to: "/" });
    } catch (e: any) {
      fail("session_save", DIAG_CODES.session_save_failed, e?.message);
    } finally {
      endCorrelation("ok", "otp_verify finished");
      setLoading(false);
    }

  }




  return (
    <div className="mobile-shell min-h-screen">

      <div className="gradient-header px-6 pb-16 pt-14 text-primary-foreground">
        <div className="mx-auto mb-6 grid h-16 w-16 place-items-center rounded-2xl bg-white/15 backdrop-blur">
          <ShieldCheck className="h-8 w-8" />
        </div>
        <h1 className="text-center text-2xl font-black tracking-wide">
          {settings?.branding.app_name ?? "Brothers Club"}
        </h1>
        <p className="mt-2 text-center text-sm text-white/80">
          {settings?.branding.tagline ?? "Play. Win. Repeat."}
        </p>
      </div>

      <div className="-mt-8 px-4">
        <div className="rounded-3xl bg-card p-6 shadow-card">
          {step === "phone" ? (
            <>
              <h2 className="text-lg font-bold">Sign in with Mobile</h2>
              <p className="mt-1 text-sm text-muted-foreground">We'll send you a one-time password.</p>
              <div className="mt-5 flex items-center gap-2 rounded-xl border border-input bg-background px-3 py-2">
                <Phone className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium text-muted-foreground">+91</span>
                <Input
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="9999999999"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  className="border-0 bg-transparent px-1 text-base focus-visible:ring-0"
                />
              </div>


              <Button onClick={sendOtp} disabled={loading} className="mt-5 h-12 w-full gradient-primary text-base font-bold shadow-purple">
                {loading ? "Sending..." : "Send OTP"}
              </Button>
              <p className="mt-4 text-center text-xs text-muted-foreground">
                By continuing you agree to our Terms & Privacy Policy.
              </p>
            </>
          ) : (
            <>
              <h2 className="text-lg font-bold">Enter OTP</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Sent to <span className="font-medium text-foreground">{fullPhone}</span>
              </p>
              <div className="mt-5 flex justify-center">
                <InputOTP maxLength={6} value={otp} onChange={setOtp}>
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot key={i} index={i} className="h-12 w-12 text-lg" />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <Button onClick={verifyOtp} disabled={loading} className="mt-5 h-12 w-full gradient-primary text-base font-bold shadow-purple">
                {loading ? "Verifying..." : "Verify & Continue"}
              </Button>
              <button
                onClick={() => { setStep("phone"); setOtp(""); }}
                className="mt-3 w-full text-center text-sm text-primary underline-offset-4 hover:underline"
              >
                Change number
              </button>
            </>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          Trouble receiving SMS? Make sure your admin has enabled an SMS provider in the Cloud dashboard.
        </p>
      </div>
    </div>
  );
}
